import React, { useEffect, useState, useCallback, useRef } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Header } from "@/components/Header";
import { OrderCard } from "@/components/OrderCard";
import { AVAILABILITY } from "@/lib/constants";
import { toast, Toaster } from "sonner";
import { Loader2, Check, X, Package, Euro, Route, Save, Volume2, VolumeX } from "lucide-react";

export default function DriverDashboard() {
  const [driver, setDriver] = useState({ availability: "hors_ligne", whatsapp_number: "" });
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [waInput, setWaInput] = useState("");
  const prevNewCount = useRef(0);
  const audioCtxRef = useRef(null);
  const soundOnRef = useRef(true);
  const [soundOn, setSoundOn] = useState(true);

  const playBeep = useCallback(() => {
    if (!soundOnRef.current) return;
    try {
      let ctx = audioCtxRef.current;
      if (!ctx) {
        ctx = new (window.AudioContext || window.webkitAudioContext)();
        audioCtxRef.current = ctx;
      }
      if (ctx.state === "suspended") ctx.resume();
      const now = ctx.currentTime;
      [0, 0.18].forEach((t) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = 880;
        gain.gain.setValueAtTime(0.001, now + t);
        gain.gain.exponentialRampToValueAtTime(0.25, now + t + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, now + t + 0.15);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(now + t);
        osc.stop(now + t + 0.16);
      });
    } catch (e) {
      console.error("Audio beep failed:", e);
    }
  }, []);

  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    soundOnRef.current = next;
    if (next) {
      const ctx = audioCtxRef.current || new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = ctx;
      if (ctx.state === "suspended") ctx.resume();
      playBeep();
    }
  };

  const load = useCallback(async () => {
    try {
      const [d, o] = await Promise.all([api.get("/driver/status"), api.get("/orders/incoming")]);
      setDriver(d.data);
      setWaInput((prev) => (prev === "" ? d.data.whatsapp_number || "" : prev));
      const newCount = o.data.filter((x) => x.status === "Nouveau").length;
      if (newCount > prevNewCount.current) {
        toast("Nouvelle demande de course !", { duration: 4000 });
        playBeep();
      }
      prevNewCount.current = newCount;
      setOrders(o.data);
    } catch (e) {
      console.error("Dashboard load failed:", e);
    } finally {
      setLoading(false);
    }
  }, [playBeep]);

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load]);

  const setAvailability = async (availability) => {
    try {
      const { data } = await api.put("/driver/status", { availability });
      setDriver(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const saveWhatsapp = async () => {
    try {
      const { data } = await api.put("/driver/status", { whatsapp_number: waInput });
      setDriver(data);
      toast.success("Numéro WhatsApp enregistré");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/orders/${id}/status`, { status });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const newOrders = orders.filter((o) => o.status === "Nouveau");
  const active = orders.filter((o) => ["Accepté", "En cours"].includes(o.status));
  const done = orders.filter((o) => ["Livré", "Annulé"].includes(o.status));
  const deliveredToday = done.filter((o) => o.status === "Livré");
  const revenue = deliveredToday.reduce((s, o) => s + o.price, 0);
  const totalKm = deliveredToday.reduce((s, o) => s + o.distance_km, 0);

  return (
    <div className="min-h-screen bg-[#0c111c] pb-10">
      <Toaster position="top-center" theme="dark" richColors />
      <Header />
      <main className="mx-auto max-w-3xl space-y-6 px-4 py-5">
        {/* Availability */}
        <section className="rounded-2xl border border-[#1F293D] bg-[#151b2b] p-4">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="font-display text-lg font-bold text-white">Ma disponibilité</h2>
            <button
              data-testid="sound-toggle-btn"
              onClick={toggleSound}
              className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${soundOn ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-400" : "border-[#1F293D] text-slate-400"}`}
            >
              {soundOn ? <Volume2 className="h-3.5 w-3.5" /> : <VolumeX className="h-3.5 w-3.5" />}
              {soundOn ? "Son activé" : "Son coupé"}
            </button>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {Object.entries(AVAILABILITY).map(([k, v]) => {
              const active = driver.availability === k;
              return (
                <button
                  key={k}
                  data-testid={`availability-${k}-btn`}
                  onClick={() => setAvailability(k)}
                  className={`flex flex-col items-center gap-1.5 rounded-xl border py-3 transition-all ${
                    active ? `${v.ring} ${v.color}` : "border-[#1F293D] text-slate-500 hover:border-slate-600"
                  }`}
                >
                  <span className={`h-3 w-3 rounded-full ${v.dot} ${active && k === "disponible" ? "radar" : ""}`} />
                  <span className="text-xs font-bold">{v.label}</span>
                </button>
              );
            })}
          </div>
          <div className="mt-3 flex items-center gap-2">
            <input
              data-testid="whatsapp-number-input"
              value={waInput}
              onChange={(e) => setWaInput(e.target.value)}
              placeholder="Mon numéro WhatsApp (ex: 33612345678)"
              className="flex-1 rounded-xl border border-[#1F293D] bg-[#0c111c] px-3 py-2.5 text-sm text-white placeholder:text-slate-600 focus:border-[#ff6a00] focus:outline-none"
            />
            <button
              data-testid="save-whatsapp-btn"
              onClick={saveWhatsapp}
              className="flex items-center gap-1.5 rounded-xl bg-[#ff6a00] px-3 py-2.5 text-sm font-bold text-white hover:bg-[#e85f00]"
            >
              <Save className="h-4 w-4" />
            </button>
          </div>
        </section>

        {/* Stats */}
        <section className="grid grid-cols-3 gap-3">
          <Stat icon={Package} label="Courses" value={deliveredToday.length} />
          <Stat icon={Euro} label="Espèces" value={`${revenue}€`} />
          <Stat icon={Route} label="Distance" value={`${totalKm.toFixed(1)} km`} />
        </section>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#ff6a00]" /></div>
        ) : (
          <>
            <Group title="Nouvelles demandes" count={newOrders.length} accent>
              {newOrders.length === 0 ? (
                <Empty text="Aucune nouvelle demande." />
              ) : (
                newOrders.map((o) => (
                  <OrderCard
                    key={o.id}
                    order={o}
                    isDriver
                    actions={
                      <div className="mt-3 grid grid-cols-2 gap-2">
                        <button
                          data-testid={`accept-order-btn-${o.id}`}
                          onClick={() => changeStatus(o.id, "Accepté")}
                          className="flex items-center justify-center gap-1.5 rounded-xl bg-emerald-600 py-2.5 text-sm font-bold text-white hover:bg-emerald-500"
                        >
                          <Check className="h-4 w-4" /> Accepter
                        </button>
                        <button
                          data-testid={`reject-order-btn-${o.id}`}
                          onClick={() => changeStatus(o.id, "Annulé")}
                          className="flex items-center justify-center gap-1.5 rounded-xl border border-rose-500/40 bg-rose-500/10 py-2.5 text-sm font-bold text-rose-400 hover:bg-rose-500/20"
                        >
                          <X className="h-4 w-4" /> Refuser
                        </button>
                      </div>
                    }
                  />
                ))
              )}
            </Group>

            <Group title="Courses en cours" count={active.length}>
              {active.length === 0 ? (
                <Empty text="Aucune course active." />
              ) : (
                active.map((o) => (
                  <OrderCard
                    key={o.id}
                    order={o}
                    isDriver
                    actions={
                      <div className="mt-3">
                        {o.status === "Accepté" && (
                          <button
                            data-testid={`pickup-order-btn-${o.id}`}
                            onClick={() => changeStatus(o.id, "En cours")}
                            className="w-full rounded-xl bg-amber-600 py-2.5 text-sm font-bold text-white hover:bg-amber-500"
                          >
                            Marquer comme récupéré
                          </button>
                        )}
                        {o.status === "En cours" && (
                          <button
                            data-testid={`deliver-order-btn-${o.id}`}
                            onClick={() => changeStatus(o.id, "Livré")}
                            className="w-full rounded-xl bg-emerald-600 py-2.5 text-sm font-bold text-white hover:bg-emerald-500"
                          >
                            Marquer comme livré
                          </button>
                        )}
                      </div>
                    }
                  />
                ))
              )}
            </Group>

            {done.length > 0 && (
              <Group title="Historique" count={done.length}>
                {done.map((o) => <OrderCard key={o.id} order={o} isDriver />)}
              </Group>
            )}
          </>
        )}
      </main>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value }) => (
  <div className="rounded-2xl border border-[#1F293D] bg-[#151b2b] p-3 text-center">
    <Icon className="mx-auto mb-1 h-4 w-4 text-[#ff6a00]" />
    <div className="font-mono text-lg font-bold text-white">{value}</div>
    <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
  </div>
);

const Group = ({ title, count, accent, children }) => (
  <section className="space-y-3">
    <div className="flex items-center gap-2">
      <h2 className="font-display text-xl font-bold text-white">{title}</h2>
      <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${accent ? "bg-[#ff6a00] text-white" : "bg-[#1F293D] text-slate-300"}`}>
        {count}
      </span>
    </div>
    {children}
  </section>
);

const Empty = ({ text }) => (
  <div className="rounded-2xl border border-[#1F293D] bg-[#151b2b] p-6 text-center text-sm text-slate-500">{text}</div>
);
